import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Field } from '~/components/shared/Field';
import { UPDATE_BUSINESS_BRANDING } from '~/services/onboarding';
import { StepComponentProps } from '../types';

export const UpdateBusinessBrand = ({
  business,
  moveNextStep,
  submitForm,
  setSubmitForm,
  setDisable,
}: StepComponentProps) => {
  const { t } = useTranslation('onboarding');
  const [updateBusinessBrand, { loading }] = useMutation(
    UPDATE_BUSINESS_BRANDING,
    {
      onCompleted: (data) => {
        if (data.response.hasError) {
          console.error(data.response.message);
          setSubmitForm(false);
        } else {
          moveNextStep(data.response.data);
        }
      },
      onError: (error) => {
        console.error(error);
        setSubmitForm(false);
      },
    },
  );

  const { register, getValues, watch, setValue } = useForm({
    defaultValues: {
      business_id: business?._id || '',
      personality_description:
        business?.branding?.personality_description || '',
      tone: business?.branding?.tone || '',
      buyer_persona_description:
        business?.branding?.buyer_persona_description || '',
      company_values: business?.branding?.company_values || '',
    },
  });

  useEffect(() => {
    if (submitForm) {
      const data = getValues();
      updateBusinessBrand({
        variables: {
          input: {
            ...data,
          },
        },
      });
    }
  }, [submitForm]);

  useEffect(() => {
    if (business.business_name && business.agent_name && business.agent_type) {
      setDisable(false);
    } else {
      setDisable(true);
    }
  }, []);

  const watchedFields = watch([
    'buyer_persona_description',
    'company_values',
    'tone',
    'company_values',
  ]);

  useEffect(() => {
    const allFilled = watchedFields.every(
      (field) => field && field.trim() !== '',
    );
    setDisable(!allFilled);
  }, [watchedFields]);

  return (
    <div className="flex flex-col gap-4">
      {loading && (
        <span className="loading loading-dots loading-md self-center text-primary"></span>
      )}
      <h1 className="text-3xl font-bold">{t('business_brand.title')}</h1>
      <p className="text-sm text-base-content">{t('business_brand.description')}</p>
      <Field
        enablePulseAnimation={true}
        useSuggestions
        watch={watch}
        setValue={setValue}
        register={register}
        label={t('business_brand.personality_description')}
        fieldName="personality_description"
        type="textarea"
        placeholder={t('business_brand.personality_description_placeholder')}
      />
      <Field
        useSuggestions
        watch={watch}
        setValue={setValue}
        register={register}
        label={t('business_brand.tone')}
        fieldName="tone"
        type="textarea"
        placeholder={t('business_brand.tone_placeholder')}
      />
      <Field
        useSuggestions
        watch={watch}
        setValue={setValue}
        register={register}
        label={t('business_brand.buyer_persona_description')}
        fieldName="buyer_persona_description"
        type="textarea"
        placeholder={t('business_brand.buyer_persona_description_placeholder')}
      />
      <Field
        useSuggestions
        watch={watch}
        setValue={setValue}
        register={register}
        label={t('business_brand.company_values')}
        fieldName="company_values"
        type="textarea"
        placeholder={t('business_brand.company_values_placeholder')}
      />
    </div>
  );
};
